import mapboxgl from "mapbox-gl";
import MapboxDirections from "@mapbox/mapbox-gl-directions/dist/mapbox-gl-directions";
import MapboxLanguage from "@mapbox/mapbox-gl-language";

export default class MapService {
  constructor(accessToken, mapElementId) {
    this.accessToken = accessToken;
    this.mapElementId = mapElementId;
    this.map = null;
    this.initialized = false;
    this.showDirection = false;
  }

  destroy() {
    if (!this.initialized) return;

    this.map.remove();
    this.initialized = false;
  }

  init(markersData, showDirection) {
    if (this.initialized) return;

    this.showDirection = showDirection;

    const style = document.createElement("style");
    style.innerHTML = `
      .mapbox-directions-instructions {
        display: none;
      }
    `;
    document.head.appendChild(style);

    mapboxgl.accessToken = this.accessToken;

    // Zentrum der Karte ermitteln
    let centerLat = 0;
    let centerLng = 0;
    let bounds = null;

    let validMarkers = Array.isArray(markersData) ? markersData : [markersData];
    validMarkers = validMarkers.filter(
      (marker) =>
        marker.location &&
        marker.location.lat &&
        marker.location.lon &&
        (marker.price_min_tax != null || marker.price_min != null)
    );

    if (validMarkers.length === 0) {
      console.error("No valid markers with lat and lon provided.");
      return;
    }

    validMarkers.forEach((marker) => {
      centerLat += marker.location.lat;
      centerLng += marker.location.lon;
    });

    centerLat = centerLat / validMarkers.length;
    centerLng = centerLng / validMarkers.length;
    // Calculate bounds to fit all markers with padding
    if (validMarkers.length > 1) {
      bounds = new mapboxgl.LngLatBounds();
      validMarkers.forEach((marker) => {
        bounds.extend([marker.location.lon, marker.location.lat]);
      });
    }

    // Karte initialisieren
    this.map = new mapboxgl.Map({
      container: this.mapElementId,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [centerLng, centerLat],
      maxZoom: 20,
      zoom: 12,
      bounds: bounds,
      language: lang,
    });
    // fitBounds: bounds ? {
    //   bounds: bounds,
    //   padding: 20 // Add padding in pixels around the bounds
    // } : undefined,

    this.images = [
      { url: "/build/images/map-point.png", id: "point-marker" },
      { url: "/build/images/map-package.png", id: "house-marker" },
    ];

    let self = this;
    this.map.on("load", () => {
      this.images.forEach((img, index) => {
        self.map.loadImage(img.url, function (error, res) {
          self.map.addImage(img.id, res);
          //   if(index === self.images.length - 1) {
          //     // Nur wenn alle Bilder geladen sind, Marker hinzufügen
          //     // self._addMarkers(validMarkers);
          //     console.log("addedMarkers")
          //   }
        });
      });
      const languageControl = new MapboxLanguage({
        language: lang,
      });
      this.map.addControl(languageControl, "bottom-right");

      setTimeout(() => {
        this._addMarkers(validMarkers);
        if (validMarkers.length > 1) {
          this.map.zoomOut();
        }
      }, 300); // Verzögerung, um sicherzustellen, dass die Bilder geladen sind
    });

    this.initialized = true;
  }

  _addMarkers(markers) {
    const SPIDERFY_RADIUS = 0.00015; // Radius, wie stark die Punkte aufgespreizt werden
    const spiderfiedPoints = {}; // Zwischenspeicher für Duplikat-Koordinaten

    const features = markers.map((marker, index) => {
      const coordKey = `${marker.location.lon},${marker.location.lat}`;
      let coordinates = [marker.location.lon, marker.location.lat];

      if (spiderfiedPoints[coordKey]) {
        const theta = (spiderfiedPoints[coordKey] * 45 * Math.PI) / 180;
        coordinates = [
          marker.location.lon + SPIDERFY_RADIUS * Math.cos(theta),
          marker.location.lat + SPIDERFY_RADIUS * Math.sin(theta),
        ];
        spiderfiedPoints[coordKey]++;
      } else {
        spiderfiedPoints[coordKey] = 1;
      }

      return {
        type: "Feature",
        id: marker.id || `marker-${index}`,
        geometry: {
          type: "Point",
          coordinates: coordinates,
        },
        properties: {
          id: marker.id ?? `marker-${index}`,
          name: marker.name,
          url: marker.url,
          portal:
            marker.map_portal ??
            ((marker.portals ?? []).includes(portal)
              ? portal
              : marker.main_portal),
          cover: marker.cover ? marker.cover.filename : null,
          address: `${marker.street} ${marker.street_no}, ${marker.zipcode} ${marker.city_name}`,
          price_show: Math.round(marker.price_min_tax ?? marker.price_min ?? 0),
          price_min_tax: marker.price_min_tax ?? marker.price_min,
          capacity: `${marker.capacity_max}`,
          dog_count: marker.rental_units
            ? marker.rental_units[0]?.dog_count
            : 0,
          type: marker.rental_type,
        },
      };
    });
    // this.map.loadImage(this.userIcon, (error, image) => {
    //   this.map.addImage("user-marker", image);
    //   this.map.loadImage(this.houseIcon, (err, houseImage) => {
    //     this.map.addImage("house-marker", houseImage);
    //   });
    // });

    if (features.length > 0 && !this.showDirection) {
      this._addGeoJsonSourceAndLayers(features);
    }

    if (features.length === 1 && this.showDirection) {
      this._addDirections(features[0]); // Directions mit dem ersten Marker
      this._addSinglePopup(features[0]);
    }

    // Event-Listener hinzufügen
    this._addEventListeners();
  }

  _addSinglePopup(feature) {
    const properties = feature.properties;
    const dogCount = this.getDogCount(properties.dog_count);
    const popupContent = `
      <div class="popup-content">
        <h3>${properties.name}</h3>
         <div class="flex gap-2 items-center">
           <p>👥 ${properties.capacity}</p>
           <div>${dogCount}</div>
        </div>
        <p>
          ${
            properties.price_min_tax
              ? global.Translator.trans(
                  "price.from_pice_per_night",
                  { price: amountToHuman(properties.price_min_tax) },
                  "js"
                )
              : ""
          }
        </p>
        </div>
    `;
    const popup = new mapboxgl.Popup({ offset: 25 })
      .setLngLat(feature.geometry.coordinates)
      .setHTML(popupContent)
      .addTo(this.map);
  }

  _addGeoJsonSourceAndLayers(features) {
    // Datenquelle hinzufügen
    this.map.addSource("accommodations", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: features,
      },
      cluster: true,
      clusterMaxZoom: 17,
      clusterRadius: 50,
    });

    this.map.addLayer({
      id: "clusters",
      type: "circle",
      source: "accommodations",
      filter: ["has", "point_count"],
      paint: {
        "circle-color": [
          "step",
          ["get", "point_count"],
          "#1d2939",
          10,
          "#181f2a",
          30,
          "#101828",
        ],
        "circle-radius": ["step", ["get", "point_count"], 20, 10, 30, 30, 40],
      },
    });

    // Cluster-Anzahl-Layer hinzufügen
    this.map.addLayer({
      id: "cluster-count",
      type: "symbol",
      source: "accommodations",
      filter: ["has", "point_count"],
      layout: {
        "text-field": "{point_count_abbreviated}",
        "text-size": 14,
      },
      paint: {
        "text-color": "#fff",
      },
    });

    this.map.addLayer({
      id: "unclustered-point",
      type: "symbol",
      source: "accommodations",
      filter: ["!", ["has", "point_count"]],
      layout: {
        // Setzt ein Icon für den Marker
        "icon-image": "house-marker", // Name des geladenen Icons
        "icon-size": 0.6, // Skaliere das Icon auf die gewünschte Größe

        "text-field": ["concat", ["to-string", ["get", "price_show"]], " €"],
        "text-size": 16,
        "text-anchor": "top", // Text oberhalb des Icons positionieren
        "text-offset": [0, -0.2], // Verschiebt den Text weiter nach oben
      },
      paint: {
        "text-color": "#fff", // Farbe des Preises
        "text-halo-color": "#101828", // Hintergrund/Umrandungsfarbe
        "text-halo-width": 3, // Umrandungsbreite
        "text-halo-blur": 1, // Weiche Umrandung
      },
    });
  }

  _addEventListeners() {
    // Zoom zum Cluster bei Klick
    this.map.on("click", "clusters", (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ["clusters"],
      });
      const clusterId = features[0].properties.cluster_id;

      this.map
        .getSource("accommodations")
        .getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;

          this.map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom,
          });
        });
    });
    // Tooltip für einzelne Marker
    this.map.on("click", "unclustered-point", (e) => {
      const coordinates = e.features[0].geometry.coordinates.slice();
      const properties = e.features[0].properties;
      // Popup-Inhalt erstellen
      const coverUrl =
        image_base_url + properties.id + "/s/" + properties.cover + ".jpg";
      const dogCount = this.getDogCount(properties.dog_count);
      const popupContent = `
                ${
                  properties.cover
                    ? `<div class="d-flex">
                    <picture class="flex-grow-1">
                        <source srcset="${coverUrl.replace(
                          ".jpg",
                          ".webp"
                        )}" type="image/webp">
                        <img src="${coverUrl}" alt="${
                          properties.name
                        }" style="max-width:100%;height:auto;margin-top:8px;">
                    </picture>`
                    : ""
                }
                    <div class="popup-content">
                      ${properties.portal ? `<svg role="img" aria-label="${properties.portal}" width="100" height="40" style="max-width:100px;max-height:40px"><use href="/build/images/logo-sprite.svg#${properties.portal.toLowerCase()}"></use></svg>` : ""}
                        <h3>${properties.name}</h3>
                        <div class="flex gap-2 items-center">
                           <p>👥 ${properties.capacity}</p>
                           <div>${dogCount}</div>
                        </div>
                        <p>
                          ${
                            properties.price_min_tax
                              ? global.Translator.trans(
                                  "price.from_pice_per_night",
                                  {
                                    price: amountToHuman(
                                      properties.price_min_tax
                                    ),
                                  },
                                  "js"
                                )
                              : ""
                          }
                        </p>
                    </div>
                    <a target="_blank" href="${properties.url}" class="full"></a>
                </div>
            `;

      // Popup erstellen und anzeigen
      new mapboxgl.Popup()
        .setLngLat(coordinates)
        .setHTML(popupContent)
        .addTo(this.map);
    });

    // Cursor-Stil ändern
    this.map.on("mouseenter", "clusters", () => {
      this.map.getCanvas().style.cursor = "pointer";
    });

    this.map.on("mouseleave", "clusters", () => {
      this.map.getCanvas().style.cursor = "";
    });

    this.map.on("mouseenter", "unclustered-point", () => {
      this.map.getCanvas().style.cursor = "pointer";
    });

    this.map.on("mouseleave", "unclustered-point", () => {
      this.map.getCanvas().style.cursor = "";
    });
  }
  _addDirections(firstMarker) {
    // Mapbox Directions initialisieren
    this.directions = new MapboxDirections({
      accessToken: this.accessToken,
      unit: "metric",
      profile: "mapbox/driving",
      interactive: false, // dont set to interactive, because destination position keeps changing
      //   placeholderOrigin: '',
      controls: {
        inputs: true,
        instructions: true,
        profileSwitcher: false,
      },
      language: lang, // Sprache auf Deutsch einstellen
    });

    // Directions control zur Karte hinzufügen
    this.map.addControl(this.directions, "top-left");

    // Zielpunkt für die Route setzen (latitude, longitude des ersten Markers)
    if (firstMarker) {
      this.directions.setDestination(
        firstMarker.properties.address || firstMarker.geometry.coordinates
      ); // Zielpunkt ist der erste Marker
      this.map.removeLayer("directions-destination-point");
      this.map.addLayer({
        id: "directions-destination-point",
        type: "symbol",
        source: "directions:markers",
        layout: {
          "icon-image": "house-marker", // Benutzerdefiniertes Haus-Icon
          "icon-size": 0.4,
        },
      });
      this.directions.on("route", () => {
        // Ursprungspunkt manuell aktualisieren

        if (this.map.getLayer("directions-origin-point")) {
          this.map.removeLayer("directions-origin-point");

          this.map.addLayer({
            id: "directions-origin-point",
            type: "symbol",
            source: "directions:markers",
            layout: {
              "icon-image": "point-marker", // Benutzerdefiniertes User-Icon
              "icon-size": 0.4,
            },
          });
        }

        // Zielpunkt manuell aktualisieren
        // if (this.map.getLayer("directions-destination-point")) {
        //   this.map.removeLayer("directions-destination-point");
        //   this.map.addLayer({
        //     id: "directions-destination-point",
        //     type: "symbol",
        //     source: "directions:markers",
        //     layout: {
        //       "icon-image": "house-marker", // Benutzerdefiniertes Haus-Icon
        //       "icon-size": 0.4,
        //     },
        //   });
        // }
        // console.log(this.map.getStyle().layers);
      });
    }
  }

  resize() {
    if (this.map) {
      this.map.resize();
    }
  }

  getDogCount(dogCount) {
    if (dogCount == null) {
      return "";
    }

    return `
    <div class="badge badge--success badge--xs badge--pill">
      <span>
       <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_7168_64948)">
          <path d="M0 4.06671C0 4.55957 0.131696 5.03224 0.366117 5.38075C0.600537 5.72925 0.918479 5.92504 1.25 5.92504C1.58152 5.92504 1.89946 5.72925 2.13388 5.38075C2.3683 5.03224 2.5 4.55957 2.5 4.06671C2.5 3.57385 2.3683 3.10117 2.13388 2.75267C1.89946 2.40416 1.58152 2.20837 1.25 2.20837C0.918479 2.20837 0.600537 2.40416 0.366117 2.75267C0.131696 3.10117 0 3.57385 0 4.06671Z" fill="#027A48"/>
          <path d="M3.65412 3.4125C4.41662 3.4125 4.80829 2.5 4.77496 1.66667C4.73746 0.708333 4.20829 0 3.55412 0C2.81246 0.0291667 2.43329 0.945833 2.46662 1.75417C2.49996 2.67083 3.01246 3.4125 3.65412 3.4125Z" fill="#027A48"/>
          <path d="M7.5 4.06671C7.5 4.55957 7.6317 5.03224 7.86612 5.38075C8.10054 5.72925 8.41848 5.92504 8.75 5.92504C9.08152 5.92504 9.39946 5.72925 9.63388 5.38075C9.8683 5.03224 10 4.55957 10 4.06671C10 3.57385 9.8683 3.10117 9.63388 2.75267C9.39946 2.40416 9.08152 2.20837 8.75 2.20837C8.41848 2.20837 8.10054 2.40416 7.86612 2.75267C7.6317 3.10117 7.5 3.57385 7.5 4.06671Z" fill="#027A48"/>
          <path d="M6.34572 3.4125C6.98738 3.4125 7.49572 2.67083 7.53322 1.74583C7.56238 0.966667 7.19572 0.0291667 6.44572 0C5.79155 0 5.26238 0.704167 5.22488 1.66667C5.19155 2.46667 5.57905 3.4125 6.34572 3.4125Z" fill="#027A48"/>
          <path d="M6.62495 5.69998C6.45828 4.86664 5.94162 3.97498 4.99995 3.97498C4.05828 3.97498 3.54162 4.86664 3.37495 5.69998C3.25885 6.00805 3.0668 6.2818 2.81662 6.49581C2.39995 6.91248 1.94995 7.41248 1.94995 8.16248C1.94995 9.82914 4.42495 10.0083 4.99995 10.0083C6.47078 10.0083 8.04995 9.42914 8.04995 8.16248C8.04995 7.42081 7.59162 6.93331 7.18328 6.49581C6.93311 6.2818 6.74105 6.00805 6.62495 5.69998Z" fill="#027A48"/>
          </g>
          <defs>
          <clipPath id="clip0_7168_64948">
          <rect width="10" height="10" fill="white"/>
          </clipPath>
          </defs>
        </svg>
      </span>
      <span>${dogCount}</span>
    </div>
  `;
  }
}
